import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import moment from 'moment';
import { getLastPrice, getHostName, formatCurrency } from '../../../utils/Utils';
import { ProductService } from '../../../service/ProductService';
import { mapValue, mrpValue } from './productSignals';

// Per-seller violator breakdown for a brand-protection product row.
// Competitors are lazy-loaded on expand via getCompetitors (the list payload no
// longer ships the full competitors array — only flat min/max/count + unauthorized_count).
export default function ViolatorRows({ row, t }) {
  const [sellers, setSellers] = useState(null); // null = loading
  const [visible, setVisible] = useState(10);

  useEffect(() => {
    let active = true;
    const end = new Date();
    const start = moment(end).subtract(7, 'days').toDate();
    new ProductService().getCompetitors(row.id, start, end)
      .then(c => { if (active) setSellers(c || []); })
      .catch(() => { if (active) setSellers([]); });
    return () => { active = false; };
  }, [row.id]);

  if (sellers === null) {
    return <div style={{ padding: 12, textAlign: 'center' }}><ProgressSpinner style={{ width: 28, height: 28 }} strokeWidth="4" /></div>;
  }
  if (!sellers.length) {
    return <div style={{ color: '#6b7280', padding: 8 }}>{t('product.no_violator_detail')}</div>;
  }

  // Treat 0/missing MAP/MRP as "not configured" → no violation, no highlight.
  const rawMap = mapValue(row), rawMrp = mrpValue(row);
  const map = rawMap > 0 ? rawMap : null;
  const mrp = rawMrp > 0 ? rawMrp : null;
  const priceOf = (s) => {
    const lp = getLastPrice(s.prices);
    return lp && lp.value != null ? lp.value : (s.price != null ? s.price : null);
  };
  const nameOf = (s) => (s.name && s.name !== '' ? s.name : getHostName(s.product_url));
  const currency = row?.violation_strategy?.currency;
  // In-stock only (stock >= 1, price > 0) to stay consistent with the list min/max.
  const inStock = (s) => { const lp = getLastPrice(s.prices); return !!(lp && lp.stock > 0 && lp.value != null && lp.value > 0); };
  // Exclude the MAP/MRP reference rows (is_map — the limits we set, not sellers).
  const sorted = sellers.filter(s => !s.is_map && inStock(s))
    .sort((a, b) => priceOf(a) - priceOf(b));
  const isViolation = (s) => {
    const p = priceOf(s);
    return p != null && ((map != null && p < map) || (mrp != null && p > mrp));
  };
  const statusBody = (s) => {
    const p = priceOf(s);
    if (map != null && p != null && p < map) return <span className="pux-chip pux-chip-red">{t('price_violation.map_abbreviation')} ▼</span>;
    if (mrp != null && p != null && p > mrp) return <span className="pux-chip pux-chip-red">{t('price_violation.mrp_abbreviation')} ▲</span>;
    return <span className="pux-chip pux-chip-green">{t('product.compliant')}</span>;
  };
  // The product's set MAP/MRP — shown as a reference banner, not a seller row.
  // Always show both; an unset limit (0/missing) reads as "not set".
  const limit = (label, value) => (
    <span>{label}: <b style={{ color: value != null ? 'inherit' : '#9ca3af' }}>
      {value != null ? formatCurrency(value, currency) : t('product.not_set')}
    </b></span>
  );
  const refBanner = (
    <div className="pux-maprow">
      <i className="pi pi-flag" />
      {limit(t('price_violation.map_abbreviation'), map)}
      {limit(t('price_violation.mrp_abbreviation'), mrp)}
    </div>
  );
  if (!sorted.length) {
    return <div>{refBanner}<div style={{ color: '#6b7280', padding: 8 }}>{t('product.no_violator_detail')}</div></div>;
  }
  return (
    <div>
      {refBanner}
      <DataTable value={sorted.slice(0, visible)} responsiveLayout="scroll" className="p-datatable-sm"
                 rowClassName={(s) => ({ 'pux-violator-row': isViolation(s) })}>
        <Column body={(s) => s.product_url
          ? <a href={s.product_url} target="_blank" rel="noreferrer">{nameOf(s)}</a>
          : nameOf(s)} header={t('product.seller')} />
        <Column body={(s) => formatCurrency(priceOf(s), currency)} header={t('product.price')} />
        <Column body={statusBody} header={t('product.status')} />
      </DataTable>
      {sorted.length > visible && (
        <button type="button" className="pux-loadmore" onClick={() => setVisible(v => v + 20)}>
          {t('product.load_more', { count: sorted.length - visible })}
        </button>
      )}
    </div>
  );
}
